/**
 * The shared ColorPicker speaks hex; several API resources store `rgb(r,g,b)`
 * so the value can go straight into CSS. Colours cross that boundary here.
 */

const RGB_STRING = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i;

const toChannel = (value: number): number =>
	Math.min(255, Math.max(0, Math.round(value)));

const toHexPair = (value: number): string =>
	toChannel(value).toString(16).padStart(2, "0").toUpperCase();

/** `#RRGGBB` (or `RRGGBB`) to `rgb(r, g, b)`. Invalid input yields null. */
export const hexToRgbString = (hex: string): string | null => {
	const value = hex.trim().replace(/^#/, "");

	if (!/^[0-9a-f]{6}$/i.test(value)) {
		return null;
	}

	const parsed = parseInt(value, 16);

	return `rgb(${(parsed >> 16) & 255}, ${(parsed >> 8) & 255}, ${parsed & 255})`;
};

/** `rgb(r, g, b)` to `#RRGGBB`. Invalid input yields null. */
export const rgbStringToHex = (rgb: string): string | null => {
	const match = rgb.trim().match(RGB_STRING);

	if (!match) {
		return null;
	}

	const [red, green, blue] = match.slice(1, 4).map(Number);

	return `#${toHexPair(red)}${toHexPair(green)}${toHexPair(blue)}`;
};
